import React from 'react';
import { makeStyles, Tooltip, IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowDropDownCircleSharpIcon from '@material-ui/icons/ArrowDropDownCircleSharp';
import { Coordinate } from '../../VideoPlayer/VideoTypes';
import TouchIcon from '@material-ui/icons/TouchApp';
import LineIcon from '@material-ui/icons/LinearScale';
import WhistleIcon from '@material-ui/icons/Sports';
import IdleIcon from '@material-ui/icons/Snooze';
import BounceIcon from '@material-ui/icons/CallMissedOutgoing';
import KeeperIcon from '@material-ui/icons/AccessibilityNew';
import GoalIcon from '@material-ui/icons/Crop75';
import RollIcon from '@material-ui/icons/SubdirectoryArrowRight';
import { receiveOptions, ballInOptions, HeightOption, goalPostOptions } from './LabelSubMenu';

const useStyles = makeStyles(() => ({
  container: {
    
  },
  airItemRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '290px',
    height: '24px',
    '&:hover': {
      backgroundColor: "#f5f6fa",
    },
    fontSize: '11px',
    opacity: 0.8,
    borderTop: "4px solid #8abffccc",
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '290px',
    height: '24px',
    '&:hover': {
      backgroundColor: "#f5f6fa",
    },
    fontSize: '11px',
    opacity: 0.8,
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '290px',
    height: '32px',
    fontSize: '16px',
    borderBottom: '1px solid #444',
  },
  nextItemRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '290px',
    height: '46px',
    borderBottom: '2px dashed #ff5a0f',
    borderTop: '2px dashed #ff5a0f',
    padding: '4px 0px',
    fontSize: '13px',
    fontWeight: 'bold',
  },
  timeColumn: {
    display: 'inline',
    width: '90px',
    color: "#444",
  },
  itemColumn: {
    display: 'flex',
    width: '125px',
    color: "#444",
    alignItems: 'center',
  },
  iconColumn: {
    display: 'inline',
    width: '75px',
    color: "#444",
  },
}));

export enum BallActionType {
  TOUCH = "TOUCH",
  BOUNCE = "BOUNCE",
  ROLL = "ROLL",
  BALL_OUT = "BALL_OUT",
  BALL_IN = "BALL_IN",
  GAME_PAUSE = "GAME_PAUSE",
  GAME_PLAY = "GAME_PLAY",
  IDLE = "IDLE",
  END = "END",
  KEEPER_HOLD = "KEEPER_HOLD",
  KEEPER_TOUCH = "KEEPER_TOUCH",
  KEEPER_RELEASE = "KEEPER_RELEASE",
  GOAL_POST = "GOAL_POST",
};

export type BallAction = {
  frame_id: number;
  type: BallActionType;
  was_in_air: boolean;
  position: Coordinate;
};

type Props = {
  actions: BallAction[];
  frameId: number;
  fps: number;
  deleteAction: (action: BallAction) => void;
  seekToAction: (action: BallAction) => void;
}

const showPrevItems: number = 4;
const showNextItems: number = 5;
// Ball Z value when on the ground (mirrors receiveOptions "Ground" preset)
const ballGroundZ: number = 0.11;

const pad = (n: number, width: number, z: string = '0') => {
  z = z || '0';
  let nStr = n.toString()
  return nStr.length >= width ? n : new Array(width - nStr.length + 1).join(z) + n;
}

const frameIdToHmsms = (totalMs: number) => {
  const ms: number = Math.floor(totalMs % 1000);
  const seconds: number = Math.floor(totalMs / 1000 % 60);
  const minutes: number = Math.floor((totalMs / 1000 / 60) % 60);
  const hours: number = Math.floor((totalMs / 1000 / 3600));

  return pad(hours, 2) + ":" + pad(minutes, 2) + ":" + pad(seconds, 2) + "." + pad(ms, 3);
};

export const getActionColor = (type: BallActionType) => {
  switch(type) {
    case BallActionType.TOUCH:
      return "#a0a0a0";
    case BallActionType.BOUNCE:
      return "#7a7a7a";
    case BallActionType.ROLL:
      return "#676";
    case BallActionType.BALL_OUT:
      return "#c51a1a";
    case BallActionType.BALL_IN:
      return "#24e645";
    case BallActionType.GAME_PAUSE:
      return "#204bda";
    case BallActionType.GAME_PLAY:
      return "#24e6ac";
    case BallActionType.IDLE:
      return "#db871a";
    case BallActionType.END:
      return "#000";
    case BallActionType.KEEPER_HOLD:
      return "#cc0";
    case BallActionType.KEEPER_TOUCH:
      return "#ff8";
    case BallActionType.KEEPER_RELEASE:
      return "#cc0";
    case BallActionType.GOAL_POST:
      return "#506075";
  }
}

export const getActionIcon = (type: BallActionType, props: any = {}) => {
  switch(type) {
    case BallActionType.TOUCH:
      return <TouchIcon {...props} />;
    case BallActionType.BOUNCE:
      return <BounceIcon {...props} />;
    case BallActionType.ROLL:
      return <RollIcon {...props} />;
    case BallActionType.BALL_OUT:
      return <LineIcon {...props} />;
    case BallActionType.BALL_IN:
      return <LineIcon {...props} />;
    case BallActionType.GAME_PAUSE:
      return <WhistleIcon {...props} />;
    case BallActionType.GAME_PLAY:
      return <WhistleIcon {...props} />;
    case BallActionType.IDLE:
      return <IdleIcon {...props} />;
    case BallActionType.END:
      return <IdleIcon {...props} />;
    case BallActionType.KEEPER_HOLD:
      return <KeeperIcon {...props} />;
    case BallActionType.KEEPER_TOUCH:
      return <KeeperIcon {...props} />;
    case BallActionType.KEEPER_RELEASE:
      return <KeeperIcon {...props} />;
    case BallActionType.GOAL_POST:
      return <GoalIcon {...props} />;
  }
}

export const getActionLabelByType = (type: BallActionType) => {
  switch(type) {
    case BallActionType.TOUCH:
      return "Touch";
    case BallActionType.BOUNCE:
      return "Bounce";
    case BallActionType.ROLL:
      return 'Roll';
    case BallActionType.BALL_OUT:
      return "Out of field";
    case BallActionType.BALL_IN:
      return "Back in field";
    case BallActionType.GAME_PAUSE:
      return "Game paused";
    case BallActionType.GAME_PLAY:
      return "Game resumes";
    case BallActionType.IDLE:
      return "Ball lays idle";
    case BallActionType.END:
      return "Task end";
    case BallActionType.KEEPER_HOLD:
      return "Keeper Hold";
    case BallActionType.KEEPER_TOUCH:
      return "Change Direction";
    case BallActionType.KEEPER_RELEASE:
      return "Keeper Release";
    case BallActionType.GOAL_POST:
      return "Goal Post";
  }
}

const getActionLabel = (action: BallAction) => {
  switch(action.type) {
    case BallActionType.TOUCH:
      const receiveOption: HeightOption | undefined = receiveOptions.find((x: HeightOption) =>
        x.value === Number(action.position.z) ? x : undefined
      );
      
      // Show the generic "Touch" label instead of "Ground". Elevated touches still show their height.
      if(receiveOption && receiveOption.value !== ballGroundZ) {
        return receiveOption.label;
      }
      break;
    case BallActionType.BALL_IN:
      const ballInOption: HeightOption | undefined = ballInOptions.find((x: HeightOption) =>
        x.value === Number(action.position.z) ? x : undefined
      );
      if(ballInOption) {
        return ballInOption.label;
      }
      break;
    case BallActionType.GOAL_POST:
      const goalOption: HeightOption | undefined = goalPostOptions.find((x: HeightOption) =>
        x.value === Number(action.position.z) ? x : undefined
      );
      if(goalOption) {
        return goalOption.label;
      }
      break;
    default:
      break;
  }
  return getActionLabelByType(action.type)
}

const BallActionList = (props: Props) => {
  const classes = useStyles();
  const { 
    actions,
    frameId,
    fps,
    deleteAction,
    seekToAction,
  } = props;
  
  // Filter & sort our lists
  const prevActionListSorted: BallAction[] = actions
    .filter((a) => a?.frame_id < frameId)
    .sort((a,b) => b?.frame_id - a?.frame_id);
  
  const nextActionListSorted: BallAction[] = actions
    .filter((a) => a?.frame_id >= frameId)
    .sort((a,b) => a?.frame_id - b?.frame_id);

  // Calculate how many next items we have to show
  const nextItemCount: number = prevActionListSorted.length < showPrevItems 
    ? showNextItems + showPrevItems - prevActionListSorted.length 
    : showNextItems;
  const nextActionList: BallAction[] = nextActionListSorted.slice(0, nextItemCount);

  // Calculate how many previous items we have to show
  const prevItemCount: number = (showPrevItems+showNextItems)-nextActionList.length;
  const prevActionList: BallAction[] = prevActionListSorted.slice(0, prevItemCount).reverse();

  const iconProps = {
    fontSize: "small",
  };

  // Create our mapped action lists
  let actionId: number = 0;
  const allActionsList: BallAction[] = prevActionList.concat(nextActionList);
  const allActions = allActionsList.map((value: BallAction) => {
    const isNext: boolean = actionId === prevActionList.length;
    const wasInAir: boolean = value.was_in_air;
    return <div 
      className={isNext ? classes.nextItemRow : wasInAir ? classes.airItemRow : classes.itemRow} 
      key={actionId++}
      style={{
        borderRight: '10px solid ' + getActionColor(value.type),
      }}
    >
      <div className={classes.timeColumn}>
        {frameIdToHmsms(value.frame_id * (1000/fps))} 
      </div>
      <div className={classes.itemColumn}>
        {getActionIcon(value.type, iconProps)} {getActionLabel(value)} 
      </div>
      <div className={classes.iconColumn} style={{textAlign: 'right',}}>
      { isNext &&
          <Tooltip title="Delete Action" arrow>
              <IconButton 
                size="small" 
                onClick={() => deleteAction(value)}
              >
                <DeleteIcon />
              </IconButton>
          </Tooltip>
        }
        <Tooltip title="Seek video to Action" arrow>
          <IconButton 
            size="small" 
            onClick={() => seekToAction(value)}
          >
            <ArrowDropDownCircleSharpIcon />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  });

  return <div className={classes.container}>
    <div className={classes.titleRow}>
      <div className={classes.timeColumn}>
        <b>Time</b>
      </div>
      <div className={classes.itemColumn}>
        <b>Action</b>
      </div>
      <div className={classes.iconColumn}>
      </div>
    </div>
    {allActions}
  </div>
}

export default BallActionList;